import React, {useState, useEffect} from 'react'
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  Alert,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@material-ui/core'
import {Icon} from '@iconify/react'
import Page from '../components/Page'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import userAPI from 'utils/api/users'
import storage from 'utils/storage'
import {logSuccessAction, ACTION_CATEGORIES} from 'utils/actionLogger'
import QrReader from 'react-qr-reader'
import Bugsnag from '@bugsnag/js'

const moment = require('moment-timezone')
moment().tz('Asia/Manila').format()

export default function AdminCompany() {
  // State for managing different sections
  const [openVideoDialog, setOpenVideoDialog] = useState(false)
  const [openThemeSongDialog, setOpenThemeSongDialog] = useState(false)
  const [openCOCDialog, setOpenCOCDialog] = useState(false)
  const [openHandbookDialog, setOpenHandbookDialog] = useState(false)
  const [openMissionVisionDialog, setOpenMissionVisionDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState(null)
  const [cocLink, setCocLink] = useState('')
  const [cocPreview, setCocPreview] = useState(null)
  const [editCocLink, setEditCocLink] = useState('')
  const [editCocPreview, setEditCocPreview] = useState(null)
  const [videosLoading, setVideosLoading] = useState(false)
  const [cocLoading, setCocLoading] = useState(false)
  const [videoOperationLoading, setVideoOperationLoading] = useState(false)
  const [themeSongLoading, setThemeSongLoading] = useState(false)
  const [handbookLoading, setHandbookLoading] = useState(false)
  const [missionVisionLoading, setMissionVisionLoading] = useState(false)

  // State for form data
  const [videoData, setVideoData] = useState({
    title: '',
    description: '',
    youtubeId: '',
  })
  const [videos, setVideos] = useState([])
  const [themeSongData, setThemeSongData] = useState({
    title: '',
    url: '',
    link: '',
  })
  const [themeSongPreview, setThemeSongPreview] = useState(null)
  const [themeSongDialogPreview, setThemeSongDialogPreview] = useState(null)
  const [cocData, setCocData] = useState({
    title: '',
    content: '',
  })
  const [handbookData, setHandbookData] = useState({
    title: '',
    content: '',
    link: '',
  })
  const [handbookPreview, setHandbookPreview] = useState(null)
  const [handbookDialogPreview, setHandbookDialogPreview] = useState(null)
  const [missionVisionData, setMissionVisionData] = useState({
    mission: '',
    vision: '',
    coreValues: ['', '', '', '', ''],
    link: '',
  })
  const [missionVisionPreview, setMissionVisionPreview] = useState(null)
  const [missionVisionDialogPreview, setMissionVisionDialogPreview] = useState(null)

  // Add new state for video preview
  const [videoPreview, setVideoPreview] = useState(null)

  // Add new state for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [videoToDelete, setVideoToDelete] = useState(null)

  // Store selection state variables
  const [company, setCompany] = useState([])
  const [selectedCompany, setSelectedCompany] = useState()
  const [openQR, setOpenQR] = useState(false)

  // Add separate loading functions for each section
  const loadVideos = async (companyId) => {
    setVideosLoading(true)
    try {
      const videosResponse = await userAPI.get_videos_tutorial(companyId)
      if (videosResponse.ok) {
        setVideos(videosResponse.data.body)
      } else {
        setVideos([])
      }
    } catch (error) {
      setVideos([])
    }
    setVideosLoading(false)
  }

  const loadCOC = async (companyId) => {
    setCocLoading(true)
    try {
      const data = {
        company: companyId,
      }
      const getCoc = await userAPI.get_company_coc(data)
      if (getCoc.data !== null && getCoc.data.link && getCoc.data.link.trim()) {
        setCocLink(getCoc.data.link)
        const embedUrl = getCocEmbedUrl(getCoc.data.link)
        if (embedUrl) {
          setCocPreview(embedUrl)
        }
      } else {
        setCocLink('')
        setCocPreview(null)
      }
    } catch (error) {
      console.error('Error loading COC:', error)
      setCocLink('')
      setCocPreview(null)
    }
    setCocLoading(false)
  }

  const loadThemeSong = async (companyId) => {
    setThemeSongLoading(true)
    try {
      const response = await userAPI.get_theme_song({company: companyId})
      if (response.data.success) {
        setThemeSongData(response.data.data)
        if (
          response.data.data.link &&
          (isGoogleDriveUrl(response.data.data.link) ||
            isYouTubeUrl(response.data.data.link) ||
            isSpotifyUrl(response.data.data.link))
        ) {
          setThemeSongPreview(getEmbedUrl(response.data.data.link))
        }
      }
    } catch (error) {
      console.error('Error loading theme song:', error)
    }
    setThemeSongLoading(false)
  }

  const loadHandbook = async (companyId) => {
    setHandbookLoading(true)
    try {
      const response = await userAPI.get_employee_handbook({company: companyId})
      if (response.data.success) {
        setHandbookData(response.data.data)
        if (
          response.data.data.link &&
          (isGoogleDriveUrl(response.data.data.link) || isGoogleDocsUrl(response.data.data.link))
        ) {
          setHandbookPreview(getEmbedUrl(response.data.data.link))
        }
      }
    } catch (error) {
      console.error('Error loading handbook:', error)
    }
    setHandbookLoading(false)
  }

  const loadMissionVision = async (companyId) => {
    setMissionVisionLoading(true)
    try {
      const response = await userAPI.get_mission_vision({company: companyId})
      if (response.data.success) {
        setMissionVisionData(response.data.data)
        if (
          response.data.data.link &&
          (isGoogleDriveUrl(response.data.data.link) || isGoogleDocsUrl(response.data.data.link))
        ) {
          setMissionVisionPreview(getEmbedUrl(response.data.data.link))
        }
      }
    } catch (error) {
      console.error('Error loading mission vision:', error)
    }
    setMissionVisionLoading(false)
  }

  // Update loadData to use separate functions
  const loadData = async () => {
    const local_user = await storage.getUser()
    if (!local_user) return

    const userData = JSON.parse(local_user)
    setUser(userData)

    let companyToLoad = userData.company

    // Group store integration for admin users (role >= 3)
    if (userData && userData.role >= 3) {
      try {
        const storesRes = await userAPI.get_group_store(userData._id)
        if (storesRes && storesRes.status === 200 && storesRes.data?.report?.store) {
          const stores = storesRes.data.report.store
          setCompany(stores)
          const firstStore = stores[0]
          setSelectedCompany(firstStore)
          companyToLoad = firstStore
        }
      } catch (err) {
        console.error('Error fetching group stores', err)
        Bugsnag.notify(err)
      }
    }

    // Load all sections in parallel
    await Promise.all([
      loadVideos(companyToLoad),
      loadCOC(companyToLoad),
      loadThemeSong(companyToLoad),
      loadHandbook(companyToLoad),
      loadMissionVision(companyToLoad),
    ])
  }

  useEffect(() => {
    loadData()
  }, [])

  // Effect to reload data when selectedCompany changes
  useEffect(() => {
    if (user && selectedCompany && user.role >= 3) {
      Promise.all([
        loadVideos(selectedCompany),
        loadCOC(selectedCompany),
        loadThemeSong(selectedCompany),
        loadHandbook(selectedCompany),
        loadMissionVision(selectedCompany),
      ])
    }
  }, [selectedCompany])

  // Add URL validation functions
  const isYouTubeUrl = (url) => {
    if (!url || !url.trim()) return false
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/
    return youtubeRegex.test(url.trim())
  }

  const isSpotifyUrl = (url) => {
    if (!url || !url.trim()) return false
    const spotifyRegex = /^(https?:\/\/)?(www\.)?(open\.)?spotify\.com\/.+$/
    return spotifyRegex.test(url.trim())
  }

  const isGoogleDriveUrl = (url) => {
    if (!url || !url.trim()) return false
    const driveRegex = /^(https?:\/\/)?(www\.)?(drive\.google\.com)\/.+$/
    return driveRegex.test(url.trim())
  }

  const isGoogleDocsUrl = (url) => {
    if (!url || !url.trim()) return false
    const docsRegex = /^(https?:\/\/)?(www\.)?(docs\.google\.com)\/.+$/
    return docsRegex.test(url.trim())
  }

  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = url.match(regExp)
    return match && match[2].length === 11 ? match[2] : null
  }

  const extractSpotifyId = (url) => {
    // Extract Spotify track/playlist/album ID from URL
    const match =
      url.match(/track\/([a-zA-Z0-9]+)/) || url.match(/playlist\/([a-zA-Z0-9]+)/) || url.match(/album\/([a-zA-Z0-9]+)/)
    return match ? match[1] : null
  }

  const isValidUrl = (url) => {
    if (!url || !url.trim()) return false
    try {
      new URL(url.trim())
      return true
    } catch {
      return false
    }
  }

  const getEmbedUrl = (url) => {
    if (isYouTubeUrl(url)) {
      const videoId = extractYouTubeId(url)
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null
    } else if (isGoogleDriveUrl(url)) {
      // Convert Google Drive URL to embed URL
      const fileId = url.match(/[-\w]{25,}/)
      return fileId ? `https://drive.google.com/file/d/${fileId[0]}/preview` : null
    } else if (isSpotifyUrl(url)) {
      const spotifyId = extractSpotifyId(url)
      if (spotifyId) {
        // Determine if it's a track, playlist, or album
        if (url.includes('/track/')) {
          return `https://open.spotify.com/embed/track/${spotifyId}`
        } else if (url.includes('/playlist/')) {
          return `https://open.spotify.com/embed/playlist/${spotifyId}`
        } else if (url.includes('/album/')) {
          return `https://open.spotify.com/embed/album/${spotifyId}`
        }
      }
    }
    return null
  }

  const handleVideoUrlChange = (e) => {
    const url = e.target.value
    setVideoData({...videoData, youtubeId: url})

    if (isYouTubeUrl(url) || isGoogleDriveUrl(url)) {
      const embedUrl = getEmbedUrl(url)
      if (embedUrl) {
        setVideoPreview(embedUrl)
      } else {
        setVideoPreview(null)
      }
    } else {
      setVideoPreview(null)
    }
  }

  const handleAddVideo = async () => {
    const trimmedTitle = videoData.title.trim()
    const trimmedUrl = videoData.youtubeId.trim()

    if (!trimmedTitle) {
      alert('Please enter title')
      return
    }

    if (!isYouTubeUrl(trimmedUrl) && !isGoogleDriveUrl(trimmedUrl)) {
      alert('Please enter a valid YouTube or Google Drive URL')
      return
    }

    setVideoOperationLoading(true)
    try {
      const companyToUpdate = user.role >= 3 && selectedCompany ? selectedCompany : user.company
      const data = {
        title: videoData.title,
        youtubeId: videoData.youtubeId,
        description: videoData.description,
        uid: user._id,
        store: companyToUpdate,
      }

      const upload = await userAPI.post_videos_tutorial(data)
      console.log(upload)
      if (upload.ok) {
        // Log successful video creation
        logSuccessAction({
          user: user._id,
          company: companyToUpdate,
          userDisplay: {
            name: user.displayName,
            email: user.email,
          },
          role: user.role,
          action: 'Create Video Tutorial',
          category: ACTION_CATEGORIES.COMPANY_CONTENT,
          details: `Successfully created new video tutorial "${videoData.title}" for ${companyToUpdate}. URL: ${
            videoData.youtubeId
          }${videoData.description ? `. Description: ${videoData.description}` : ''}`,
        })

        await loadVideos(companyToUpdate)
        window.alert('Successfully added')
        setOpenVideoDialog(false)
        setVideoData({title: '', description: '', youtubeId: ''})
      } else {
        throw new Error(upload.error)
      }
    } catch (error) {
      alert('Failed to add video')
    }
    setVideoOperationLoading(false)
  }

  const handleEditVideo = (id) => {
    const video = videos.find((v) => v._id === id)
    setVideoData({
      title: video.title,
      description: video.description,
      youtubeId: video.youtubeId,
      id: video._id,
    })
    setVideoPreview(getEmbedUrl(video.youtubeId))
    setOpenVideoDialog(true)
  }

  const handleDeleteClick = (id) => {
    setVideoToDelete(id)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (videoToDelete) {
      // Find the video being deleted for logging
      const videoToDeleteData = videos.find((v) => v._id === videoToDelete)
      const companyToUpdate = user.role >= 3 && selectedCompany ? selectedCompany : user.company

      setVideoOperationLoading(true)
      try {
        const r = await userAPI.delete_video_tutorial(videoToDelete)
        if (r.status === 200) {
          // Log successful video deletion
          if (videoToDeleteData) {
            logSuccessAction({
              user: user._id,
              company: companyToUpdate,
              userDisplay: {
                name: user.displayName,
                email: user.email,
              },
              role: user.role,
              action: 'Delete Video Tutorial',
              category: ACTION_CATEGORIES.COMPANY_CONTENT,
              details: `Successfully deleted video tutorial "${videoToDeleteData.title}" for ${companyToUpdate}. URL: ${
                videoToDeleteData.youtubeId
              }${videoToDeleteData.description ? `. Description: ${videoToDeleteData.description}` : ''}`,
            })
          }

          await loadVideos(companyToUpdate)
          alert('Video deleted')
        } else {
          alert(r.message)
        }
      } catch (error) {
        alert('Failed to delete video')
      }
      setDeleteDialogOpen(false)
      setVideoToDelete(null)
      setVideoOperationLoading(false)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false)
    setVideoToDelete(null)
  }

  const handleUpdateVideo = async () => {
    const trimmedTitle = videoData.title.trim()
    const trimmedUrl = videoData.youtubeId.trim()

    if (!trimmedTitle) {
      alert('Please enter title')
      return
    }

    if (!isYouTubeUrl(trimmedUrl) && !isGoogleDriveUrl(trimmedUrl)) {
      alert('Please enter a valid YouTube or Google Drive URL')
      return
    }

    // Get original video for edit logging
    const originalVideo = videos.find((v) => v._id === videoData.id)

    setVideoOperationLoading(true)
    try {
      const data = {
        title: videoData.title,
        youtubeId: videoData.youtubeId,
        description: videoData.description,
      }

      const r = await userAPI.edit_video_tutorial(videoData.id, data)
      const companyToUpdate = user.role >= 3 && selectedCompany ? selectedCompany : user.company
      if (r.status === 200) {
        // Log successful video edit
        if (originalVideo) {
          const changes = []
          if (videoData.title !== originalVideo.title) {
            changes.push(`Title: "${originalVideo.title}" → "${videoData.title}"`)
          }
          if (videoData.youtubeId !== originalVideo.youtubeId) {
            changes.push(`URL: "${originalVideo.youtubeId}" → "${videoData.youtubeId}"`)
          }
          if (videoData.description !== originalVideo.description) {
            changes.push(`Description: "${originalVideo.description || 'None'}" → "${videoData.description || 'None'}"`)
          }

          logSuccessAction({
            user: user._id,
            company: companyToUpdate,
            userDisplay: {
              name: user.displayName,
              email: user.email,
            },
            role: user.role,
            action: 'Edit Video Tutorial',
            category: ACTION_CATEGORIES.COMPANY_CONTENT,
            details: `Successfully edited video tutorial "${originalVideo.title}" for ${companyToUpdate}${
              changes.length > 0 ? `. Changes: ${changes.join(', ')}` : ''
            }`,
          })
        }

        await loadVideos(companyToUpdate)
        alert('Video updated')
        setOpenVideoDialog(false)
        setVideoData({title: '', description: '', youtubeId: ''})
      }
    } catch (error) {
      alert('Failed to update video')
    } finally {
      setVideoOperationLoading(false)
    }
  }

  const handleThemeSongLinkChange = (e) => {
    const url = e.target.value.trim()
    setThemeSongData({...themeSongData, link: url})

    if (isGoogleDriveUrl(url) || isYouTubeUrl(url) || isSpotifyUrl(url)) {
      const embedUrl = getEmbedUrl(url)
      if (embedUrl) {
        setThemeSongDialogPreview(embedUrl)
      } else {
        setThemeSongDialogPreview(null)
      }
    } else {
      setThemeSongDialogPreview(null)
    }
  }

  const handleUpdateThemeSong = async () => {
    if (!themeSongData.link.trim()) {
      alert('Please enter a valid link')
      return
    }

    if (
      !isGoogleDriveUrl(themeSongData.link) &&
      !isYouTubeUrl(themeSongData.link) &&
      !isSpotifyUrl(themeSongData.link)
    ) {
      alert('Please enter a valid Google Drive, YouTube, or Spotify link')
      return
    }

    setThemeSongLoading(true)
    try {
      const companyToUpdate = user.role >= 3 && selectedCompany ? selectedCompany : user.company
      const data = {
        company: companyToUpdate,
        link: themeSongData.link,
      }

      const response = await userAPI.set_theme_song(data)
      if (response.data.success) {
        // Log successful theme song update
        const oldLink = themeSongPreview ? themeSongData.link : 'None'
        logSuccessAction({
          user: user._id,
          company: companyToUpdate,
          userDisplay: {
            name: user.displayName,
            email: user.email,
          },
          role: user.role,
          action: 'Update Theme Song',
          category: ACTION_CATEGORIES.COMPANY_CONTENT,
          details: `Successfully updated company theme song for ${companyToUpdate}. Link: "${oldLink}" → "${themeSongData.link}"`,
        })

        alert('Theme song updated successfully')
        setOpenThemeSongDialog(false)
        setThemeSongDialogPreview(null)
        await loadThemeSong(companyToUpdate)
      } else {
        alert(response.data.message || 'Failed to update theme song')
      }
    } catch (error) {
      alert('Failed to update theme song')
    }
    setThemeSongLoading(false)
  }

  const handleOpenCOCDialog = () => {
    setEditCocLink(cocLink)
    setEditCocPreview(cocPreview)
    setOpenCOCDialog(true)
  }

  const handleCloseCOCDialog = () => {
    setOpenCOCDialog(false)
    setEditCocLink('')
    setEditCocPreview(null)
  }

  const handleCocLinkChange = (e) => {
    const url = e.target.value.trim()
    setEditCocLink(url)

    if (isValidUrl(url)) {
      const embedUrl = getCocEmbedUrl(url)
      if (embedUrl) {
        setEditCocPreview(embedUrl)
      } else {
        setEditCocPreview(null)
      }
    } else {
      setEditCocPreview(null)
    }
  }

  const handleUpdateCOC = async () => {
    const trimmedLink = editCocLink.trim()
    if (!trimmedLink) {
      alert('Please enter the COC link')
      return
    }

    if (!isValidUrl(trimmedLink)) {
      alert('Please enter a valid URL')
      return
    }

    setLoading(true)
    if (user.company === undefined) {
      await loadData()
      alert('Something went wrong please try again')
      setLoading(false)
      return
    }

    try {
      const companyToUpdate = user.role >= 3 && selectedCompany ? selectedCompany : user.company
      const data = {
        company: companyToUpdate,
        link: trimmedLink,
      }

      const result = await userAPI.set_company_coc(data)
      if (result.data.success) {
        // Log successful COC update
        const oldLink = cocLink || 'None'
        logSuccessAction({
          user: user._id,
          company: companyToUpdate,
          userDisplay: {
            name: user.displayName,
            email: user.email,
          },
          role: user.role,
          action: cocLink ? 'Update Code of Conduct' : 'Create Code of Conduct',
          category: ACTION_CATEGORIES.COMPANY_CONTENT,
          details: `Successfully ${
            cocLink ? 'updated' : 'created'
          } code of conduct for ${companyToUpdate}. Link: "${oldLink}" → "${trimmedLink}"`,
        })

        alert(result.data.msg)
        handleCloseCOCDialog()
        await loadCOC(companyToUpdate)
      } else {
        alert(result.data.msg)
      }
    } catch (error) {
      alert('Failed to update COC')
    }
    setLoading(false)
  }

  const handleHandbookLinkChange = (e) => {
    const url = e.target.value.trim()
    setHandbookData({...handbookData, link: url})

    if (isGoogleDriveUrl(url) || isGoogleDocsUrl(url)) {
      const embedUrl = getEmbedUrl(url)
      if (embedUrl) {
        setHandbookDialogPreview(embedUrl)
      } else {
        setHandbookDialogPreview(null)
      }
    } else {
      setHandbookDialogPreview(null)
    }
  }

  const handleUpdateHandbook = async () => {
    if (!handbookData.link.trim()) {
      alert('Please enter a valid Google Drive or Docs link')
      return
    }

    if (!isGoogleDriveUrl(handbookData.link) && !isGoogleDocsUrl(handbookData.link)) {
      alert('Please enter a valid Google Drive or Docs link')
      return
    }

    setHandbookLoading(true)
    try {
      const companyToUpdate = user.role >= 3 && selectedCompany ? selectedCompany : user.company
      const data = {
        company: companyToUpdate,
        link: handbookData.link,
      }

      const response = await userAPI.set_employee_handbook(data)
      if (response.data.success) {
        // Log successful handbook update
        const oldLink = handbookPreview ? handbookData.link : 'None'
        logSuccessAction({
          user: user._id,
          company: companyToUpdate,
          userDisplay: {
            name: user.displayName,
            email: user.email,
          },
          role: user.role,
          action: 'Update Employee Handbook',
          category: ACTION_CATEGORIES.COMPANY_CONTENT,
          details: `Successfully updated employee handbook for ${companyToUpdate}. Link: "${oldLink}" → "${handbookData.link}"`,
        })

        alert('Employee handbook updated successfully')
        setOpenHandbookDialog(false)
        setHandbookDialogPreview(null)
        await loadHandbook(companyToUpdate)
      } else {
        alert(response.data.message || 'Failed to update employee handbook')
      }
    } catch (error) {
      alert('Failed to update employee handbook')
    }
    setHandbookLoading(false)
  }

  const handleMissionVisionLinkChange = (e) => {
    const url = e.target.value.trim()
    setMissionVisionData({...missionVisionData, link: url})

    if (isGoogleDriveUrl(url) || isGoogleDocsUrl(url)) {
      const embedUrl = getEmbedUrl(url)
      if (embedUrl) {
        setMissionVisionDialogPreview(embedUrl)
      } else {
        setMissionVisionDialogPreview(null)
      }
    } else {
      setMissionVisionDialogPreview(null)
    }
  }

  const handleUpdateMissionVision = async () => {
    if (!missionVisionData.link.trim()) {
      alert('Please enter a valid Google Drive or Docs link')
      return
    }

    if (!isGoogleDriveUrl(missionVisionData.link) && !isGoogleDocsUrl(missionVisionData.link)) {
      alert('Please enter a valid Google Drive or Docs link')
      return
    }

    setMissionVisionLoading(true)
    try {
      const companyToUpdate = user.role >= 3 && selectedCompany ? selectedCompany : user.company
      const data = {
        company: companyToUpdate,
        link: missionVisionData.link,
      }

      const response = await userAPI.set_mission_vision(data)
      if (response.data.success) {
        // Log successful mission vision update
        const oldLink = missionVisionPreview ? missionVisionData.link : 'None'
        logSuccessAction({
          user: user._id,
          company: user.role >= 3 && selectedCompany ? selectedCompany : user.company,
          userDisplay: {
            name: user.displayName,
            email: user.email,
          },
          role: user.role,
          action: 'Update Mission Vision',
          category: ACTION_CATEGORIES.COMPANY_CONTENT,
          details: `Successfully updated mission, vision & core values for ${companyToUpdate}. Link: "${oldLink}" → "${missionVisionData.link}"`,
        })

        alert('Mission vision core values updated successfully')
        setOpenMissionVisionDialog(false)
        setMissionVisionDialogPreview(null)
        await loadMissionVision(companyToUpdate)
      } else {
        alert(response.data.message || 'Failed to update mission vision core values')
      }
    } catch (error) {
      alert('Failed to update mission vision core values')
    }
    setMissionVisionLoading(false)
  }

  const handleCloseVideoDialog = () => {
    setOpenVideoDialog(false)
    setVideoPreview(null)
    setVideoData({title: '', description: '', youtubeId: ''})
  }

  const handleUploadNewVideo = () => {
    setOpenVideoDialog(true)
    setVideoData({title: '', description: '', youtubeId: ''})
  }

  // Add function to get embed URL for COC
  const getCocEmbedUrl = (url) => {
    if (isGoogleDriveUrl(url)) {
      const fileId = url.match(/[-\w]{25,}/)
      return fileId ? `https://drive.google.com/file/d/${fileId[0]}/preview` : null
    }
    // For other document types, we'll use the direct URL
    return url
  }

  // Store selection handlers
  const handleStoreChange = async (event, newValue) => {
    if (!newValue) return // Don't proceed if no value is selected

    setLoading(true)
    setSelectedCompany(newValue)

    // Load all sections with the new company
    await Promise.all([
      loadVideos(newValue),
      loadCOC(newValue),
      loadThemeSong(newValue),
      loadHandbook(newValue),
      loadMissionVision(newValue),
    ])
    setLoading(false)
  }

  const handleOpenQRModal = () => {
    setOpenQR(true)
  }

  const handleCloseQR = () => {
    setOpenQR(false)
  }

  const handleError = (err) => {
    alert(err)
  }

  const handleScan = async (res) => {
    if (res) {
      try {
        const url = new URL(res)
        const pathSegments = url.pathname.split('/')
        const id = pathSegments[1]
        const data = {
          uid: user._id,
          storeid: id,
        }

        const result = await userAPI.post_group_store(data)
        if (result.status === 200) {
          setOpenQR(false)
          alert('Success')
          // Reload the data to include the new store
          await loadData()
        } else {
          alert('Something went wrong please try again later!')
        }
      } catch (error) {
        alert('Invalid QR code format')
      }
    } else {
      alert('No link found in the response.')
    }
  }

  return (
    <Page title="Admin Company">
      <Container maxWidth="xl">
        {/* Store Selection for Admin Users */}
        {user && user.role >= 3 && (
          <Box sx={{mb: 4, display: 'flex', justifyContent: 'center'}}>
            <Box display="flex" alignItems="center" sx={{minWidth: 350}}>
              <Autocomplete
                options={company
                  .slice()
                  .sort((a, b) => a.trim().replace(/\s+/g, ' ').localeCompare(b.trim().replace(/\s+/g, ' ')))}
                value={selectedCompany || company[0] || ''}
                onChange={handleStoreChange}
                loading={loading}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search and Select Store"
                    variant="outlined"
                    fullWidth
                    placeholder="Type to search stores..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{flexGrow: 1}}
                isOptionEqualToValue={(option, value) => option === value}
                getOptionLabel={(option) => option || ''}
                noOptionsText="No stores found"
                clearOnEscape
                autoHighlight
                openOnFocus
                filterOptions={(options, {inputValue}) => {
                  return options.filter((option) => option.toLowerCase().includes(inputValue.toLowerCase()))
                }}
              />
              <IconButton color="primary" aria-label="add store" onClick={handleOpenQRModal} sx={{ml: 1}}>
                <AddIcon />
              </IconButton>
            </Box>
          </Box>
        )}

        {/* Video Guidelines Section */}
        <Box sx={{mb: 6}}>
          <Typography
            variant="h4"
            sx={{
              mb: 3,
              fontWeight: 500,
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Icon icon="mdi:video" style={{fontSize: '28px'}} />
            Video Guidelines
          </Typography>
          <Grid container spacing={3}>
            {/* Video Tutorials Card */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                    <Typography variant="h6">Video Tutorials</Typography>
                    <Button startIcon={<AddIcon />} onClick={handleUploadNewVideo} disabled={videosLoading}>
                      Add Video
                    </Button>
                  </Box>
                  {videosLoading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                      <CircularProgress />
                    </Box>
                  ) : videos.length === 0 ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                      <Typography variant="body2" color="text.secondary">
                        No video tutorials have been uploaded yet.
                      </Typography>
                    </Box>
                  ) : (
                    <List>
                      {videos.map((video) => (
                        <ListItem key={video._id}>
                          <ListItemText
                            primary={video.title.trim() || 'No title'}
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  {video.description.trim() || 'No description'}
                                </Typography>
                                {video.youtubeId && video.youtubeId.trim() ? (
                                  <Box sx={{mt: 1, width: '90%', height: '200px', mr: 2}}>
                                    <iframe
                                      src={getEmbedUrl(video.youtubeId)}
                                      width="100%"
                                      height="100%"
                                      frameBorder="0"
                                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                      allowFullScreen
                                    />
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                    No video URL provided
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              alignItems: 'center',
                              width: '10%',
                              height: '100%',
                            }}
                          >
                            <IconButton
                              edge="end"
                              onClick={() => handleEditVideo(video._id)}
                              disabled={videoOperationLoading}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={() => handleDeleteClick(video._id)}
                              disabled={videoOperationLoading}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Theme Song Card */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                    <Typography variant="h6">Company Theme Song</Typography>
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => setOpenThemeSongDialog(true)}
                      disabled={themeSongLoading}
                    >
                      Edit
                    </Button>
                  </Box>
                  {themeSongLoading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                      <CircularProgress />
                    </Box>
                  ) : themeSongData.link ? (
                    <Box>
                      <Typography variant="subtitle2" color="text.primary" sx={{mb: 2}}>
                        Current Theme Song:
                      </Typography>
                      <Box sx={{width: '100%', height: '100px', mb: 2}}>
                        <iframe
                          src={themeSongPreview}
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{
                          wordBreak: 'break-all',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(themeSongData.link, '_blank')}
                      >
                        {themeSongData.link}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No theme song has been set yet.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Company Guidelines Section */}
        <Box>
          <Typography
            variant="h4"
            sx={{
              mb: 3,
              fontWeight: 500,
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Icon icon="mdi:office-building" style={{fontSize: '28px'}} />
            Company Guidelines
          </Typography>
          <Grid container spacing={3}>
            {/* Code of Conduct Card */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                    <Typography variant="h6">Code of Conduct</Typography>
                    <Button
                      startIcon={cocLink ? <EditIcon /> : <AddIcon />}
                      onClick={handleOpenCOCDialog}
                      disabled={cocLoading}
                    >
                      {cocLink ? 'Edit' : 'Add'}
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Last updated: {new Date().toLocaleDateString()}
                  </Typography>
                  {cocLoading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                      <CircularProgress />
                    </Box>
                  ) : cocLink ? (
                    <Box sx={{mt: 2}}>
                      <Typography variant="subtitle2" color="text.primary">
                        Current COC Link:
                      </Typography>
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{
                          wordBreak: 'break-all',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(cocLink, '_blank')}
                      >
                        {cocLink}
                      </Typography>
                      {cocPreview && (
                        <Box sx={{mt: 2, width: '100%', height: '400px'}}>
                          <iframe
                            src={cocPreview}
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </Box>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No Code of Conduct has been added yet.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Employee Handbook Card */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                    <Typography variant="h6">Employee Handbook</Typography>
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => setOpenHandbookDialog(true)}
                      disabled={handbookLoading}
                    >
                      Edit
                    </Button>
                  </Box>
                  {handbookLoading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                      <CircularProgress />
                    </Box>
                  ) : handbookData.link ? (
                    <Box>
                      <Typography variant="subtitle2" color="text.primary" sx={{mb: 2}}>
                        Current Employee Handbook:
                      </Typography>
                      <Box sx={{width: '100%', height: '400px', mb: 2}}>
                        <iframe
                          src={handbookPreview}
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{
                          wordBreak: 'break-all',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(handbookData.link, '_blank')}
                      >
                        {handbookData.link}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No employee handbook has been set yet.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Mission, Vision & Values Card */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                    <Typography variant="h6">Mission, Vision & Core Values</Typography>
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => setOpenMissionVisionDialog(true)}
                      disabled={missionVisionLoading}
                    >
                      Edit
                    </Button>
                  </Box>
                  {missionVisionLoading ? (
                    <Box sx={{display: 'flex', justifyContent: 'center', p: 3}}>
                      <CircularProgress />
                    </Box>
                  ) : missionVisionData.link ? (
                    <Box>
                      <Typography variant="subtitle2" color="text.primary" sx={{mb: 2}}>
                        Current Mission, Vision & Core Values:
                      </Typography>
                      <Box sx={{width: '100%', height: '400px', mb: 2}}>
                        <iframe
                          src={missionVisionPreview}
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{
                          wordBreak: 'break-all',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(missionVisionData.link, '_blank')}
                      >
                        {missionVisionData.link}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No mission, vision & core values have been set yet.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Video Tutorial Dialog */}
        <Dialog open={openVideoDialog} onClose={handleCloseVideoDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {videoData.id ? 'Edit Video' : 'Add New Video'}
            <IconButton
              aria-label="close"
              onClick={handleCloseVideoDialog}
              sx={{position: 'absolute', right: 8, top: 8}}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Title"
              fullWidth
              value={videoData.title}
              onChange={(e) => setVideoData({...videoData, title: e.target.value})}
            />
            <TextField
              margin="dense"
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={videoData.description}
              onChange={(e) => setVideoData({...videoData, description: e.target.value})}
            />
            <TextField
              margin="dense"
              label="Video URL (YouTube or Google Drive)"
              fullWidth
              value={videoData.youtubeId}
              onChange={handleVideoUrlChange}
              helperText="Enter YouTube or Google Drive URL"
            />
            {videoPreview && (
              <>
                {isGoogleDriveUrl(videoData.youtubeId) && (
                  <Alert severity="info" sx={{mt: 2}}>
                    Note: Users need to enable cookies in their browser to view Google Drive videos.
                  </Alert>
                )}
                <Box sx={{mt: 2, width: '100%', height: '300px'}}>
                  <iframe
                    src={videoPreview}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseVideoDialog} disabled={videoOperationLoading}>
              Cancel
            </Button>
            <Button
              onClick={videoData.id ? handleUpdateVideo : handleAddVideo}
              variant="contained"
              color="primary"
              disabled={
                videoOperationLoading ||
                !videoData.title.trim() ||
                (!isYouTubeUrl(videoData.youtubeId) && !isGoogleDriveUrl(videoData.youtubeId))
              }
            >
              {videoOperationLoading ? <CircularProgress size={24} /> : videoData.id ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Theme Song Dialog */}
        <Dialog
          open={openThemeSongDialog}
          onClose={() => {
            setOpenThemeSongDialog(false)
            setThemeSongDialogPreview(null)
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Update Theme Song
            <IconButton
              aria-label="close"
              onClick={() => {
                setOpenThemeSongDialog(false)
                setThemeSongDialogPreview(null)
              }}
              sx={{position: 'absolute', right: 8, top: 8}}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>Paste the Google Drive, YouTube, or Spotify link of your theme song.</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Media Link"
              fullWidth
              value={themeSongData.link}
              onChange={handleThemeSongLinkChange}
              helperText="Enter a valid Google Drive, YouTube, or Spotify link"
            />
            {themeSongDialogPreview && (
              <>
                {(isGoogleDriveUrl(themeSongData.link) || isSpotifyUrl(themeSongData.link)) && (
                  <Alert severity="info" sx={{mt: 2}}>
                    Note: Users need to enable cookies in their browser to view Google Drive and Spotify content.
                  </Alert>
                )}
                <Box sx={{mt: 2, width: '100%', height: isSpotifyUrl(themeSongData.link) ? '80px' : '100px'}}>
                  <iframe
                    src={themeSongDialogPreview}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenThemeSongDialog(false)
                setThemeSongDialogPreview(null)
              }}
              disabled={themeSongLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateThemeSong}
              variant="contained"
              color="primary"
              disabled={
                themeSongLoading ||
                (!isGoogleDriveUrl(themeSongData.link) &&
                  !isYouTubeUrl(themeSongData.link) &&
                  !isSpotifyUrl(themeSongData.link))
              }
            >
              {themeSongLoading ? <CircularProgress size={24} /> : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Code of Conduct Dialog */}
        <Dialog open={openCOCDialog} onClose={handleCloseCOCDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {cocLink ? 'Update Code of Conduct' : 'Add Code of Conduct'}
            <IconButton aria-label="close" onClick={handleCloseCOCDialog} sx={{position: 'absolute', right: 8, top: 8}}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>Paste the upload link of COC.</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="link"
              label="Link"
              type="text"
              fullWidth
              variant="standard"
              value={editCocLink}
              onChange={handleCocLinkChange}
            />
            {editCocPreview && (
              <>
                {isGoogleDriveUrl(editCocLink) && (
                  <Alert severity="info" sx={{mt: 2}}>
                    Note: Users need to enable cookies in their browser to view Google Drive documents.
                  </Alert>
                )}
                <Box sx={{mt: 2, width: '100%', height: '400px'}}>
                  <iframe
                    src={editCocPreview}
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </Box>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCOCDialog}>Cancel</Button>
            <Button
              onClick={handleUpdateCOC}
              variant="contained"
              color="primary"
              disabled={loading || !isValidUrl(editCocLink)}
            >
              {loading ? <CircularProgress size={24} /> : cocLink ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Employee Handbook Dialog */}
        <Dialog
          open={openHandbookDialog}
          onClose={() => {
            setOpenHandbookDialog(false)
            setHandbookDialogPreview(null)
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Update Employee Handbook
            <IconButton
              aria-label="close"
              onClick={() => {
                setOpenHandbookDialog(false)
                setHandbookDialogPreview(null)
              }}
              sx={{position: 'absolute', right: 8, top: 8}}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>Paste the Google Drive or Docs link of your employee handbook.</DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Document Link"
              fullWidth
              value={handbookData.link}
              onChange={handleHandbookLinkChange}
              helperText="Enter a valid Google Drive or Docs link"
            />
            {handbookDialogPreview && (
              <Box sx={{mt: 2, width: '100%', height: '400px'}}>
                <iframe
                  src={handbookDialogPreview}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenHandbookDialog(false)
                setHandbookDialogPreview(null)
              }}
              disabled={handbookLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateHandbook}
              variant="contained"
              color="primary"
              disabled={
                handbookLoading || (!isGoogleDriveUrl(handbookData.link) && !isGoogleDocsUrl(handbookData.link))
              }
            >
              {handbookLoading ? <CircularProgress size={24} /> : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Mission Vision Dialog */}
        <Dialog
          open={openMissionVisionDialog}
          onClose={() => {
            setOpenMissionVisionDialog(false)
            setMissionVisionDialogPreview(null)
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Update Mission, Vision & Core Values
            <IconButton
              aria-label="close"
              onClick={() => {
                setOpenMissionVisionDialog(false)
                setMissionVisionDialogPreview(null)
              }}
              sx={{position: 'absolute', right: 8, top: 8}}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Paste the Google Drive or Docs link of your mission, vision & core values document.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Document Link"
              fullWidth
              value={missionVisionData.link}
              onChange={handleMissionVisionLinkChange}
              helperText="Enter a valid Google Drive or Docs link"
            />
            {missionVisionDialogPreview && (
              <Box sx={{mt: 2, width: '100%', height: '400px'}}>
                <iframe
                  src={missionVisionDialogPreview}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenMissionVisionDialog(false)
                setMissionVisionDialogPreview(null)
              }}
              disabled={missionVisionLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateMissionVision}
              variant="contained"
              color="primary"
              disabled={
                missionVisionLoading ||
                (!isGoogleDriveUrl(missionVisionData.link) && !isGoogleDocsUrl(missionVisionData.link))
              }
            >
              {missionVisionLoading ? <CircularProgress size={24} /> : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete this video? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary" disabled={videoOperationLoading}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="secondary"
              variant="contained"
              disabled={videoOperationLoading}
            >
              {videoOperationLoading ? <CircularProgress size={24} /> : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* QR Scanner Dialog */}
        <Dialog open={openQR} onClose={handleCloseQR}>
          <DialogTitle>
            Scan store QR code.
            <IconButton
              aria-label="close"
              onClick={handleCloseQR}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <QrReader
              delay={3000}
              onError={handleError}
              onScan={handleScan}
              style={{width: '250px', height: '200px'}}
            />
          </DialogContent>
        </Dialog>
      </Container>
    </Page>
  )
}

